import { createContext, useContext } from 'react';
import type { UnsavedDraft } from './useUnsavedDraftGuard';
import {
  AdviseEngagement,
  AdviseSupplierContract,
  AdviseSupplierScoring,
  AdviseSupplier,
  DiscoveryAnswer,
  DiscoveryFormSection,
  MatrixCriterion,
  MatrixCriterionValue,
  MatrixFeatureHome,
  NewCurrentSupplier,
  NewMatrixCriterion,
  SupplierDirectoryRow,
  SupplierLineItemsReplacement
} from '../../../../types/advise';

export interface EngagementDiscovery {
  // Null while the form loads, and after a load fails.
  sections: DiscoveryFormSection[] | null;
  failed?: boolean;
  retry?: () => void;
  // Replaces the question's whole answer and saves it in the background; does nothing on a read-only engagement.
  answer: (questionId: string, value: DiscoveryAnswer) => void;
}

export interface EngagementFunctionalityMatrix {
  // Null until the matrix first loads, and after a load fails.
  criteria: MatrixCriterion[] | null;
  failed?: boolean;
  // Loads the matrix once the edits still saving have landed, since a Discovery answer can change its selections.
  refresh?: () => void;
  // Each shows the change at once and saves it in the background; they do nothing on a read-only engagement.
  updateCriterion: (criterionId: string, value: MatrixCriterionValue) => void;
  selectFeature: (featureId: string, selected: boolean) => void;
  // Each saves first and shows the change once saved, resolving to why it was refused, or null once saved.
  // Left out where nothing can be added, as on the share page.
  addCriterion?: (criterion: NewMatrixCriterion) => Promise<string | null>;
  addFeature?: (home: MatrixFeatureHome, label: string) => Promise<string | null>;
  removeCriterion?: (criterionId: string) => Promise<string | null>;
  removeFeature?: (featureId: string) => Promise<string | null>;
}

export interface EngagementSupplierSelection {
  // Null until load first succeeds; failed when the last load didn't.
  directory: SupplierDirectoryRow[] | null;
  failed: boolean;
  load: () => void;
  // Each supplier's supplierKey, in the order picked.
  selectedKeys: string[];
  // Shows the change at once and saves the whole shortlist in the background; does nothing on a read-only engagement.
  select: (keys: string[], selected: boolean) => void;
  // Adds a supplier, or edits the one with that id; resolves to why it was refused, or null once the directory shows it.
  saveSupplier: (supplier: Partial<AdviseSupplier>) => Promise<string | null>;
}

export interface EngagementSupplierScoring {
  // Null until the first load succeeds.
  scoring: AdviseSupplierScoring | null;
  failed: boolean;
  // Loads once the edits still saving have landed, since the shortlist may have changed on Supplier Selection.
  refresh: () => void;
  // Shows the rating at once and saves it in the background; does nothing on a read-only engagement. A rating off the
  // client average carries the note that justifies it.
  rate: (supplierId: string, functionId: string, rating: number, note?: string | null) => void;
}

export interface EngagementCurrentSuppliers {
  // Shows the change at once and saves it straight away; a refused one is put back. Does nothing on a read-only engagement.
  skip: (skipped: boolean) => void;
  // Each saves first and shows the change once saved, resolving to why it was refused, or null once saved
  // (or, for a removal answered 404, once a re-read shows it already gone).
  removeSupplier: (supplierId: string) => Promise<string | null>;
  removeInvoice: (supplierId: string, invoiceId: string) => Promise<string | null>;
  // Also posts a refusal as a snackbar, since nothing else on the card shows it.
  uploadInvoice: (supplierId: string, file: File) => Promise<string | null>;
  // Resolves to the new supplier's id once it saved, even when its invoice then failed to upload (posted as a
  // snackbar, leaving the supplier to retry from its card), or to why the supplier itself was refused.
  addSupplier: (supplier: NewCurrentSupplier, invoice: File | null) => Promise<AddedSupplier>;
  // The Invoice View's edits: each shows at once and saves in turn, and a refused one is put back. The amounts the API
  // calculates change only once it answers. Each does nothing on a read-only engagement.
  setLineInScope: (supplierId: string, invoiceId: string, lineId: string, inScope: boolean) => void;
  setLineCategory: (supplierId: string, invoiceId: string, lineId: string, category: string) => void;
  setIncludeTaxes: (supplierId: string, included: boolean) => void;
  // Sends only the fields changed; upstack_api keeps the rest as stored.
  saveContract: (supplierId: string, change: AdviseSupplierContract) => void;
  // Hand-entered spend saves first and shows once saved.
  saveManualLines: (supplierId: string, invoiceId: string | null, lines: SupplierLineItemsReplacement) => Promise<void>;
  saveMonthlyTotal: (supplierId: string, total: number) => Promise<void>;
  // Takes a supplier off its monthly total, so its most recent invoice's line items count again.
  countLineItems: (supplierId: string) => Promise<void>;
}

export interface EngagementCurrentSpend {
  // Each resolves true once saved; a refusal is shown and resolves false.
  override: (monthlyAmount: number) => Promise<boolean>;
  reset: () => Promise<boolean>;
  // Re-reads the engagement and shows its current spend as Current Suppliers now works it out.
  refresh: () => Promise<void>;
}

export interface AddedSupplier {
  supplierId: string | null;
  refusal: string | null;
  // A 422: the API refused what was sent, so sending the same again can't succeed.
  refusedAsSent: boolean;
}

export interface EngagementEditor {
  engagement: AdviseEngagement;
  // Every supplier; each panel picks the engagement's selected ones itself.
  suppliers: AdviseSupplier[];
  canEdit: boolean;
  // Shows the patch at once and saves it in the background; does nothing on a read-only engagement.
  editEngagement: (patch: Partial<AdviseEngagement>) => void;
  discovery: EngagementDiscovery;
  functionalityMatrix: EngagementFunctionalityMatrix;
  // Left out where no shortlist is built, as on the share page, which hides the Supplier Selection tab.
  supplierSelection?: EngagementSupplierSelection;
  // Left out where the Current Suppliers tab never shows, as on the share page.
  currentSuppliers?: EngagementCurrentSuppliers;
  // Left out where the engagement carries no current spend, as on the share page.
  currentSpend?: EngagementCurrentSpend;
  // UPSTACK staff only: left out for the client's own users and on the share page, which hides the tab.
  supplierScoring?: EngagementSupplierScoring;
  // Resolves true once every edit, waiting or already sending, has saved. Left out where nothing is edited.
  flushSaves?: () => Promise<boolean>;
  // Left out where nothing is edited.
  unsavedDraft?: UnsavedDraft;
}

const EngagementEditorContext = createContext<EngagementEditor | null>(null);

export const EngagementEditorProvider = EngagementEditorContext.Provider;

/** The engagement, its suppliers and how to edit it, from the nearest EngagementEditorProvider. */
export const useEngagementEditor = (): EngagementEditor => {
  const editor = useContext(EngagementEditorContext);
  if (!editor) throw new Error('useEngagementEditor must be used inside an EngagementEditorProvider');
  return editor;
};
